/**
 * The field verification queue — FR-07, §3.1.
 *
 * High-stakes fields pass human verification before they are trusted. The queue
 * plus the audit row is the CR-01 evidence chain for a field; this is the surface
 * where the second half of that pair gets created.
 */
import { z } from 'zod';
import { ContractKeySchema, HttpsUrlSchema, IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema, } from '../primitives.js';
import { ContractTypeConfirmationSchema, ContractTypeSchema, VerificationStatusSchema } from '../vocabulary.js';
import { ConversionCaveatSchema, FieldProvenanceSchema } from '../provenance.js';
/**
 * The aging signal, exactly as `clm.vw_verification_aging` emits it.
 *
 * Not a derivation this package performs, and deliberately not: the thresholds
 * (high-stakes warns at 24h and escalates at 48h, everything else warns at 168h)
 * live in the view's `CASE`, and the notification ladder reads the same view —
 * one mechanism, two uses (§3.2). A second copy of the arithmetic in TypeScript
 * is how the queue and the notifier come to disagree about whether a field is
 * late.
 *
 * The order is the `CASE`'s own severity order read the other way round, so the
 * four members are the four branches and nothing else: `Settled` (any status
 * other than `Pending`), `Escalate`, `Warn`, `Normal`
 * (`0041_field_verification.sql`).
 */
export const AGING_SIGNALS = ['Normal', 'Warn', 'Escalate', 'Settled'];
export const AgingSignalSchema = z.enum(AGING_SIGNALS);
/**
 * The name of a verifiable field, as a dotted path into
 * {@link import('./contract.js').ContractDetail}.
 *
 * Deliberately a bounded string rather than an enum. The verifiable set is
 * whatever the extraction schema populates, and that schema is versioned
 * independently (`wp-03` §3, `SCHEMA_VERSION`) — pinning an enum here would mean
 * a schema bump breaks the front-end build for a field it does not render.
 *
 * Examples: `dates.renewal_notice_date`, `key_terms.liability_cap`,
 * `personal_data.contains_personal_data`.
 */
export const FieldNameSchema = z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_]+(?:\.[a-z0-9_]+)*$/, 'expected a snake_case dotted field path');
/**
 * One row of the verification queue.
 *
 * `age_days` and `is_escalated` come from the aging signal
 * (`vw_verification_aging`), which is the same escalation mechanism the
 * notification ladder uses — one mechanism, two uses, deliberately (§3.2).
 */
export const VerificationQueueItemSchema = z.object({
    verification_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    /**
     * The type of the contract the field belongs to. Carried so a reviewer can see
     * *what* they are verifying without a second call; it drives no rule here —
     * whether a type implies a verification policy is outstanding with Sean
     * (`requirements.md` §9 item 4), and `is_high_stakes` is the only flag the
     * queue orders on.
     */
    contract_type: ContractTypeSchema,
    /** CLM-110 (R-110-23) — rendered beside `contract_type` as its `provisional` tag; drives no rule here. */
    contract_type_confirmation: ContractTypeConfirmationSchema,
    /** For display, so the queue does not need a second call per row. */
    primary_party_display_name: z.string().min(1).max(200),
    /**
     * CLM-78 (B5) — which system owns the primary party's name. The VerifyPanel
     * captions *Accept extracted* with "updates the vendor's display name" only for
     * `'CLM'` (A5); Qfly/Dynamics names are read-only caches (DR-04). Optional so
     * older fixtures and clients parse unchanged; `null` when the row has no
     * primary party.
     */
    primary_party_sor_system: z.enum(['Qfly', 'Dynamics', 'CLM']).nullable().optional(),
    /**
     * CLM-85 — the contract's conversion caveat; the queue flags `key_terms.*` rows
     * when `'tables_missing'`. Optional in the `primary_party_sor_system` precedent so
     * older fixtures and clients parse unchanged.
     */
    conversion_caveat: ConversionCaveatSchema.nullable().optional(),
    /**
     * CLM-89 (R-89-1) — the Graph `webUrl` of the document under review, so the
     * reviewer can open the PDF in SharePoint from the panel (IR-05: a link, never
     * bytes). Anchored on `v.contract_key` — an amendment's own document, not its
     * root's. `null` when intake recorded no link. Optional in the
     * `primary_party_sor_system` precedent so older fixtures and clients parse unchanged.
     */
    sharepoint_item_url: HttpsUrlSchema.nullable().optional(),
    /**
     * CLM-99 — the primary party's Dynamics billing client ID, and whether it
     * makes the client billable; optional in the `primary_party_sor_system`
     * precedent so older fixtures and clients parse unchanged.
     */
    primary_party_billing_client_id: z.string().min(1).max(64).nullable().optional(),
    primary_party_is_billable: z.boolean().nullable().optional(),
    /**
     * CLM-109 (R-109-15) — for an `additional_terms.*` row: the description the
     * model was **asked with**, resolved through the row's own
     * `extraction_audit_id` → that audit row's field-set revision →
     * `fn_extraction_field_set_at`. Not the definition in force today, which may
     * have been edited since. `null` for every governed row. Optional in the
     * `primary_party_sor_system` precedent so older fixtures parse unchanged.
     */
    field_description: z.string().min(1).max(500).nullable().optional(),
    /**
     * CLM-109 (R-109-13) — the admin field has been deactivated since. The row
     * can still be settled and still counts toward `data_trust`: the value was
     * extracted and nobody has verified it.
     */
    field_retired: z.boolean().optional(),
    /**
     * CLM-109 (R-109-12) — the model returned this many characters, over the
     * field's own bound, so `extracted_value` is `null` **without** the document
     * being silent. Settling it `Verified` would record a silence that is not
     * one. `null` otherwise.
     */
    field_over_bound_length: z.number().int().min(1).nullable().optional(),
    /**
     * CLM-108 (R-108-17) — when the policy settled this row (`AutoApproved`): the
     * `AutoApproval` row's instant. `null` for every other status. Optional in the
     * `primary_party_sor_system` precedent so older fixtures parse unchanged. The
     * row's `policy_disposition` is deliberately **not** on the wire (R-108-18): a
     * reviewer told that a pending row is "the sample" may grade it differently.
     */
    auto_approved_utc: IsoTimestampUtcSchema.nullable().optional(),
    field_name: FieldNameSchema,
    /** What the pipeline produced. `null` where it found nothing. */
    extracted_value: z.string().max(400).nullable(),
    /** What a human settled on. `null` until verified or corrected. */
    confirmed_value: z.string().max(400).nullable(),
    /** FR-07 — e.g. a renewal date on a top-concentration account. */
    is_high_stakes: z.boolean(),
    verification_status: VerificationStatusSchema,
    enqueued_utc: IsoTimestampUtcSchema,
    verified_by_upn: UpnSchema.nullable(),
    verified_utc: IsoTimestampUtcSchema.nullable(),
    escalated_utc: IsoTimestampUtcSchema.nullable(),
    age_days: z.number().int().min(0),
    /**
     * Whether this row has crossed the escalation threshold — the persisted
     * `escalated_utc` stamp **or** a live `aging_signal` of `Escalate`. Either one
     * is enough: the stamp is written by the notification service, so before that
     * lands the live signal is the only truth, and after it lands a row that
     * escalated and was then re-enqueued must not silently de-escalate.
     */
    is_escalated: z.boolean(),
    /** As the view computed it. See {@link AGING_SIGNALS}. */
    aging_signal: AgingSignalSchema,
    /**
     * Why a reviewer could not settle the field — `null` unless the status is
     * `Rejected` (`ck_field_verification_rejection_reason`). On the queue row and
     * not only in the audit log because a rejected field **stays visible**: the
     * next reviewer needs to know what stopped the last one.
     */
    rejection_reason: z.string().max(1000).nullable(),
    /**
     * Provenance of the value under review — which model produced it, under which
     * `extraction_schema_version`. A reviewer needs this: confirming a field is an
     * assertion about a specific model output, not about the field in general.
     */
    provenance: FieldProvenanceSchema,
});
//# sourceMappingURL=verification.js.map